//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  ImpactConnectionRequest,
  ImpactConnectionResponse,
} from '@apis/account-settings/impactAccountApiModel';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const IMPACT_ENDPOINT = '/api/v1/integrations/impact';

const gatewayFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${IMPACT_ENDPOINT}${endpoint}`,
    init,
    useFetchHeader,
  );
};

/**
 * Connect to Impact.com using Account SID and Auth Token
 * Follows validate-first approach with automatic cleanup on failure
 *
 * POST /api/v1/integrations/impact/connection?clientId={clientId}
 */
export const connectImpactAccount = async (
  req: ImpactConnectionRequest,
  init?: RequestInit,
): Promise<void> => {
  await gatewayFetch('/connection', {
    ...init,
    body: JSON.stringify(req),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });
};

/**
 * Disconnect from Impact.com
 * Removes connection and securely deletes all stored credentials
 *
 * DELETE /api/v1/integrations/impact/connection?clientId={clientId}
 */
export const disconnectImpactAccount = async (
  init?: RequestInit,
): Promise<void> => {
  await gatewayFetch('/connection', {
    ...init,
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'DELETE',
  });
};

/**
 * Get the current Impact.com connection
 *
 * GET /api/v1/integrations/impact/connection?clientId={clientId}
 */
export const getImpactConnection = async (
  init?: RequestInit,
): Promise<ImpactConnectionResponse> => {
  const response = await gatewayFetch('/connection', {
    ...init,
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });
  return response.json<ImpactConnectionResponse>();
};
